import { HubConnectionBuilder, LogLevel, HubConnection } from "@microsoft/signalr";

import { VueConstructor } from "vue/types/vue";
import { Guid } from "@/domain/guid";
import { Vue } from "vue-property-decorator";

/// Register types

interface TeamHub extends Vue {
	startSignalR: (onStarted: any) => any;
	stopSignalR: () => undefined | Promise<void>;
	connectToGuild: (teamID: Guid) => Promise<any>;
}

declare module "vue/types/vue" {
	interface Vue {
		$teamHub: TeamHub;
	}
}

export default {
	install(vue: VueConstructor) {
		/// Register stores

		/// Register components

		/// Register methods
		// use a new Vue instance as the interface for Vue components to receive/send SignalR events
		// this way every component can listen to events or send new events using this.$teamHub
		const hub = new vue();
		vue.prototype.$teamHub = hub;

		// Provide methods to connect/disconnect from the SignalR hub
		let connection: HubConnection;
		let startedPromise: Promise<any> | null;
		let manuallyClosed = false;

		vue.prototype.$teamHub.startSignalR = (onStarted: () => Promise<void>) => {
			connection = new HubConnectionBuilder()
				.withUrl("/api/v1/synchronization")
				.configureLogging(LogLevel.None)
				.build();

			// Forward hub events through the event, so we can listen for them in the Vue components
			connection.on("TeamSync", (teamID: Guid) => {
				hub.$emit("team-sync", teamID);
			});

			// You need to call connection.start() to establish the connection but the client wont handle reconnecting for you!
			// Docs recommend listening onclose and handling it there.
			// This is the simplest of the strategies
			function start(): any {
				startedPromise = connection.start()
					.then(() => {
						return onStarted();
					})
					.catch(err => {
						console.error("Failed to connect with hub", err);
						return new Promise((resolve, reject) => setTimeout(() => start().then(resolve).catch(reject), 5000));
					});
				return startedPromise;
			}
			connection.onclose(() => {
				if (!manuallyClosed) start();
			});

			// Start everything
			manuallyClosed = false;
			return start();
		};
		vue.prototype.$teamHub.stopSignalR = () => {
			if (!startedPromise) return;

			manuallyClosed = true;
			return startedPromise
				.then(() => connection.stop())
				.then(() => { startedPromise = null; });
		};

		// Provide methods for components to send messages back to server
		// Make sure no invocation happens until the connection is established
		vue.prototype.$teamHub.connectToGuild = async (teamID: Guid) => {
			if (!startedPromise) return;

			return await connection.invoke("ConnectToTeam", teamID);
		};
	}
};